import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Checkbox, Icon } from 'Components';
import Carousel from 'LearningManager/components/Carousel';
import CourseCard from 'LearningManager/components/CourseCard';
import AssignmentBadge from 'LearningManager/components/AssignmentBadge';
import Heading from 'LearningManager/components/Heading';
import DropdownMenu from '../Dropdown';
import {
	CourseData,
	LearningPathData
} from 'LearningManager/constants/UserDashboard.constants';
import { formatCardDate } from 'LearningManager/helpers/date';
import styles from './LearningPathCard.module.scss';

interface MenuOption {
	key: string;
	label: string;
	danger?: boolean;
}

interface Props {
	learningPath: LearningPathData;
	checked?: boolean;
	onCheck?: (id: string, checked: boolean) => void;
	menuOptions?: MenuOption[];
	menuDisabled?: boolean;
	onMenuAction?: (id: string, action: string) => void;
	onCourseClick?: (course: CourseData) => void;
	countsOnly?: boolean;
	showCards?: boolean;
}

const LearningPathCard: React.FC<Props> = ({
	learningPath,
	checked = false,
	onCheck,
	menuOptions,
	menuDisabled = false,
	onMenuAction,
	onCourseClick,
	countsOnly = false,
	showCards = false
}) => {
	const [isOpen, setIsOpen] = useState(showCards);

	const {
		id,
		title,
		statuses,
		courseCount,
		completedCount,
		completionPercentage,
		dueDate,
		startsDate,
		courses,
		learningPaths,
		individualCourses
	} = learningPath;
	const { t } = useTranslation();

	const renderCourseCard = (course: CourseData) => (
		<CourseCard
			index={course.id}
			htmlId={`lms-lp-${id}-course-${course.id}`}
			course={{ ...course, isSelected: false }}
			onClick={() => onCourseClick?.(course)}
			ctaLabel={course.ctaLabel}
			hideBadge
			hideRequiredSection
		/>
	);

	const hasGroupedContent =
		(learningPaths && learningPaths.length > 0) ||
		(individualCourses && individualCourses.length > 0);

	const learningPathCount = learningPaths?.length ?? 0;
	const individualCourseCount = individualCourses?.length ?? 0;

	const contentLabel = hasGroupedContent
		? [
			...(learningPathCount
				? [
					`${learningPathCount} ${t(
						learningPathCount === 1
							? 'learning.courseCard.learningPath'
							: 'learning.courseCard.learningPaths'
					)}`
				]
				: []),
			...(individualCourseCount
				? [
					`${individualCourseCount} ${t(
						individualCourseCount === 1
							? 'learning.courseCard.course'
							: 'learning.courseCard.courses'
					)}`
				]
				: [])
		].join(' & ')
		: `${courseCount} ${t(
			courseCount === 1
				? 'learning.courseCard.course'
				: 'learning.courseCard.courses'
		)}`;

	const metaParts = [
			contentLabel,
			[
				t('learning.courseCard.completedCount', {
					completedCount,
					courseCount
				})
			],
			...(dueDate
				? [
					t('learning.courseCard.dueDate', {
						date: formatCardDate(dueDate)
					})
				]
				: []),
			...(startsDate
				? [
					t('learning.courseCard.startsDate', {
						date: formatCardDate(startsDate)
					})
				] : [])
		];

	return (
		<div className={styles.card}>
			<div className={styles.row} onClick={() => setIsOpen((o) => !o)}>
				{onCheck && (
					<div
						className={styles.checkbox}
						onClick={(e) => e.stopPropagation()}
					>
						<Checkbox
							checked={checked}
							onChange={(e) => onCheck(id, e.target.checked)}
						/>
					</div>
				)}

				<div className={styles.body}>
					<div className={styles.header}>
						<div className={styles.heading}>
							<div className={styles.title}>{title}</div>

							<div className={styles.tags}>
								{statuses.map((s) => (
									<AssignmentBadge key={s} type={s} />
								))}
							</div>
						</div>

						{menuOptions && (
							<div onClick={(e) => e.stopPropagation()}>
								<DropdownMenu
									addDropdownClass={'learning-card-menu'}
									arrow={false}
									coverTrigger
									disabled={menuDisabled}
									onChange={(key: string) =>
										onMenuAction?.(id, key)
									}
									options={menuOptions}
								>
									<div
										className={classNames(styles.moreBtn, {
											[styles['moreBtn--disabled']]:
												menuDisabled
										})}
									>
										<Icon type="optionIcon" role="img" />
									</div>
								</DropdownMenu>
							</div>
						)}
					</div>

					<div className={styles.progressBar}>
						<div
							className={styles.progressFill}
							style={{
								width: `${Math.min(completionPercentage, 100)}%`
							}}
						/>
					</div>

					<div className={styles.footer}>
						<div className={styles.meta}>{metaParts.join(' | ')}</div>

						<span
							className={classNames(styles.viewContent, {
								[styles['viewContent--open']]: isOpen
							})}
						>
							{t(`learning.courseCard.${isOpen ? 'hideContent' : 'viewContent'}`)}{' '}
							<Icon type="chevronDown" role="img" />
						</span>
					</div>
				</div>
			</div>

			{isOpen && (
				<div className={styles.expanded}>
					{hasGroupedContent ? (
						<div className={styles.sections}>
							{learningPaths && learningPaths.length > 0 && (
								<div className={styles.container}>
									<Heading
										title={t(
											'learning.courseCard.learningPaths'
										)}
									/>
									<div className={styles.section}>
										{learningPaths.map((group) => (
											<Carousel
												key={group.label}
												data={group.courses}
												label={group.label}
												labelClassName={styles.pathLabel}
												itemsPerPage={4}
												renderItem={renderCourseCard}
											/>
										))}
									</div>
								</div>
							)}

							{individualCourses &&
								individualCourses.length > 0 && (
									<div className={styles.container}>
										<Heading
											title={t(
												'learning.courseCard.individualCourses'
											)}
										/>
										<Carousel
											data={individualCourses}
											itemsPerPage={4}
											renderItem={renderCourseCard}
										/>
									</div>
								)}
						</div>
					) : (
						<Carousel
							data={courses}
							itemsPerPage={4}
							renderItem={renderCourseCard}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default LearningPathCard;